// SPARES-PROC-SEP-001 Task 7 — Spare Buying Queue.
//
// One worklist for the spares buyer: every PR raised by auto-reorder or a
// shortage, ranked by age, so the buyer can pick up and continue quote → PO
// on the existing spare-indent flow. No quote/PO screens live here — this
// is a deep-linking queue only.

import { Card, Table, Tag, Typography } from 'antd'
import { Link } from 'react-router-dom'
import dayjs from 'dayjs'

import { useSpareBuyingQueue, type SpareBuyingQueueRow } from '@/features/stores/spareBuyingQueueApi'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

const SOURCE_COLOR: Record<SpareBuyingQueueRow['source'], string> = {
  AUTO_REORDER: 'blue',
  SHORTAGE: 'orange',
}

export default function SpareBuyingQueuePage() {
  const { data, isLoading } = useSpareBuyingQueue()

  const columns = [
    {
      title: 'PR No', dataIndex: 'pr_no', key: 'pr_no',
      render: (v: string) => (
        <Link to="/stores/purchase-indents">{v}</Link>
      ),
    },
    {
      title: 'Date', dataIndex: 'pr_date', key: 'pr_date',
      render: (v: string) => dayjs(v).format('DD-MMM-YYYY'),
    },
    {
      title: 'Age', dataIndex: 'age_days', key: 'age_days',
      render: (v: number | null) => {
        if (v == null) return <Text type="secondary">—</Text>
        return v > 7 ? <Tag color="red">{v}d</Tag> : <Text>{v}d</Text>
      },
    },
    {
      title: 'Source', dataIndex: 'source', key: 'source',
      render: (v: SpareBuyingQueueRow['source']) => (
        <Tag color={SOURCE_COLOR[v] ?? 'default'}>{v.replace(/_/g, ' ')}</Tag>
      ),
    },
    { title: 'Priority', dataIndex: 'priority', key: 'priority' },
    { title: 'Lines', dataIndex: 'line_count', key: 'line_count', align: 'right' as const },
    {
      title: 'Est. Value ₹', dataIndex: 'estimated_value', key: 'estimated_value',
      align: 'right' as const,
      render: (v: number) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      ),
    },
  ]

  return (
    <div>
      <PageHeader title="Spare Buying Queue" subtitle="Auto-reorder + shortage PRs awaiting quote → PO" />
      <Card>
        <Table<SpareBuyingQueueRow>
          rowKey="id"
          loading={isLoading}
          dataSource={data ?? []}
          columns={columns}
          pagination={{ pageSize: 50, showSizeChanger: true }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
    </div>
  )
}
